import { f0FormField } from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import SubmissionPeriodModeSelector from 'modules/planning/components/SubmissionPeriodModeSelector'

export const createGeneralInfoStepSchema = ({
  datesDisabled = false,
  canLaunchNow = true,
  autoCreateBackfillDisabled = false,
}: {
  datesDisabled?: boolean
  canLaunchNow?: boolean
  autoCreateBackfillDisabled?: boolean
} = {}) =>
  z
    .object({
      name: f0FormField(z.string().trim().min(1), {
        label: i18n.t('planning.period_wizard.general_info.name_label'),
        placeholder: i18n.t('planning.period_wizard.general_info.name_placeholder'),
      }),
      startDate: f0FormField(z.date(), {
        label: i18n.t('planning.period_wizard.general_info.start_date_label'),
        row: 'dates',
        disabled: datesDisabled,
      }),
      endDate: f0FormField(z.date(), {
        label: i18n.t('planning.period_wizard.general_info.end_date_label'),
        row: 'dates',
        disabled: datesDisabled,
      }),
      submissionMode: f0FormField(z.enum(['schedule', 'launch']), {
        label: i18n.t('planning.submission_period.form.schedule_title'),
        helpText: i18n.t('planning.submission_period.form.schedule_description'),
        fieldType: 'custom',
        disabled: ({ values }) => !values.startDate || !values.endDate,
        render: ({ value, onChange }) => (
          <SubmissionPeriodModeSelector
            value={value}
            onChange={onChange}
            canLaunchNow={canLaunchNow}
          />
        ),
      }),
      submissionStartDate: f0FormField(z.date().optional(), {
        label: i18n.t('planning.submission_period.form.start_date'),
        fieldType: 'date',
        row: 'submission-dates',
        renderIf: { fieldId: 'submissionMode', equalsTo: 'schedule' },
        disabled: ({ values }) => !values.startDate || !values.endDate,
        minDate: new Date(),
        maxDate: ({ values }) => (values.endDate instanceof Date ? values.endDate : undefined),
      }),
      submissionEndDate: f0FormField(z.date().optional(), {
        label: i18n.t('planning.submission_period.form.end_date'),
        fieldType: 'date',
        row: 'submission-dates',
        disabled: ({ values }) => !values.startDate || !values.endDate,
        minDate: ({ values }) =>
          values.submissionStartDate instanceof Date ? values.submissionStartDate : undefined,
        maxDate: ({ values }) => (values.endDate instanceof Date ? values.endDate : undefined),
      }),
      autoCreateBackfillPositions: f0FormField(z.boolean().default(false), {
        label: i18n.t('planning.period_wizard.general_info.auto_create_backfill_label'),
        helpText: i18n.t('planning.period_wizard.general_info.auto_create_backfill_description'),
        fieldType: 'checkbox',
        disabled: autoCreateBackfillDisabled,
      }),
    })
    .superRefine((values, ctx) => {
      if (!values.startDate || !values.endDate) return

      if (values.submissionMode === 'launch') {
        if (!values.submissionEndDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('common.errors.required'),
            path: ['submissionEndDate'],
          })
        }
      }

      if (values.submissionMode === 'schedule') {
        const hasStart = !!values.submissionStartDate
        const hasEnd = !!values.submissionEndDate

        if (hasStart && !hasEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('common.errors.required'),
            path: ['submissionEndDate'],
          })
        }

        if (!hasStart && hasEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('common.errors.required'),
            path: ['submissionStartDate'],
          })
        }

        if (
          hasStart &&
          hasEnd &&
          values.submissionStartDate &&
          values.submissionEndDate &&
          values.submissionEndDate <= values.submissionStartDate
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('forms.schedule.end_date_must_be_after_start_date'),
            path: ['submissionEndDate'],
          })
        }
      }
    })
